import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LifeBuoy, Phone, ExternalLink } from "lucide-react";

const BANNER = "https://images.unsplash.com/photo-1581713872605-b9dfbc84eaa4";

export default function Resources() {
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cat, setCat] = useState("");
  const [country, setCountry] = useState("");

  useEffect(() => { api.get("/resources/categories").then(({ data }) => { setCategories(data.categories || []); setCountries(data.countries || []); }).catch(() => {}); }, []);
  useEffect(() => {
    const params = {};
    if (cat) params.category = cat;
    if (country) params.country = country;
    api.get("/resources", { params }).then(({ data }) => setResources(data.resources || [])).catch(() => {});
  }, [cat, country]);

  return (
    <div>
      <div className="relative mb-8 overflow-hidden rounded-3xl border border-border/60">
        <img src={BANNER} alt="" className="h-40 w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-0 p-6 text-white">
          <h1 className="font-serif text-3xl font-semibold">Resource Center</h1>
          <p className="mt-1 max-w-md text-sm text-white/85">Trusted helplines and support organisations, tailored to your region.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Select value={country || "all"} onValueChange={(v) => setCountry(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48 rounded-full" data-testid="resource-country-select"><SelectValue placeholder="All regions" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All regions</SelectItem>{countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={cat || "all"} onValueChange={(v) => setCat(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56 rounded-full" data-testid="resource-category-select"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {resources.map((r, i) => (
          <motion.div key={r.name} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.04 }}
                      className="ambient-shadow rounded-3xl border border-border/60 bg-card p-6">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10"><LifeBuoy className="h-5 w-5 text-primary" strokeWidth={1.5} /></div>
            <h3 className="mt-4 font-serif text-lg leading-snug">{r.name}</h3>
            <p className="mt-1 text-xs text-primary">{r.category} · {r.country}</p>
            <p className="mt-2 text-sm text-muted-foreground">{r.description}</p>
            <div className="mt-4 space-y-1.5">
              {r.phone && <p className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} /> {r.phone}</p>}
              {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline" data-testid={`resource-link-${i}`}><ExternalLink className="h-4 w-4" strokeWidth={1.5} /> Visit website</a>}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
